import { api } from '@/lib/api'

/**
 * Deadline for the effective-permission fetch (P3-1, #1064).
 *
 * The shared axios instance sets no timeout, so a hung connection (captive
 * portal, dead proxy, sleeping laptop) leaves this request pending forever.
 * Anything that BLOCKS on the permission set -- the nav-v2 sidebar waits for
 * it before rendering, on purpose, so it never shows an unfiltered nav -- would
 * then wait forever too. A bounded request turns that hang into a rejection,
 * which drives permissionsStatus to 'error' and lets consumers fail closed.
 *
 * 15s is far above the observed p99 for this endpoint (a single indexed
 * role_permissions read) and well below a user's patience for a nav that
 * hasn't appeared.
 */
export const PERMISSIONS_FETCH_TIMEOUT_MS = 15_000
import type {
  LoginCredentials,
  AuthResponse,
  User,
  LogoutRequest,
  LogoutResponse,
  ActiveSession,
} from '@/types'

export interface PasswordChangePayload {
  current_password: string
  new_password: string
}

export interface PasswordChangeResponse {
  message: string
  success: boolean
}

export interface ForgotPasswordPayload {
  email: string
}

export interface ForgotPasswordResponse {
  success: boolean
  message: string
}

export interface ResetPasswordPayload {
  token: string
  new_password: string
}

export interface ResetPasswordResponse {
  success: boolean
  message: string
}

export interface TenantRegistrationPayload {
  company_name_en: string
  company_name_ar?: string | null
  slug: string
  admin_email: string
  admin_username: string
  admin_full_name: string
  admin_password: string
}

export interface TenantRegistrationResponse {
  access_token: string
  token_type: string
  tenant_id: number
  tenant_slug: string
  admin_user_id: number
  admin_email: string
}

export interface VerifyTokenResponse {
  valid: boolean
  message: string
  email: string | null
}

// S46: /auth/me/permissions response shape
export interface MyPermissionsResponse {
  user_id: number
  tenant_id: number
  role: string | null            // e.g., "site_engineer"; null if no role assigned
  permissions: string[]          // sorted alphabetical permission codes
  permission_count: number
  // S49: tenant display preferences
  tenant_calendar_preference: 'gregorian' | 'hijri' | 'both'
  tenant_default_currency: string
  // Stage 2 (Agent A): region selector for the multi-region tax engine
  tenant_region?: 'UAE' | 'KSA' | 'EGY'
}

// Module 2.1 Phase 2 P2 — email verification
export interface VerifyEmailPayload { token: string }
export interface VerifyEmailResponse {
  success: boolean
  message: string
  email: string | null
  is_verified: boolean | null
}
export interface ResendVerificationResponse {
  success: boolean
  message: string
}

// S49 + Stage 2 (Agent A): /auth/me/tenant-settings PATCH payload + response
export type TenantRegion = 'UAE' | 'KSA' | 'EGY'

export interface TenantSettingsUpdate {
  calendar_preference?: 'gregorian' | 'hijri' | 'both'
  default_currency_code?: string
  region?: TenantRegion
}

export interface TenantSettingsResponse {
  tenant_id: number
  calendar_preference: 'gregorian' | 'hijri' | 'both'
  default_currency_code: string
  region?: TenantRegion | null
}

// === Phase 2 Wave 5 Agent S1 BEGIN ===
// Login may now return an MFA challenge instead of a full Token. The FE
// type-narrows on `mfa_required` and routes to the MFA challenge step.
export interface MFAChallengeResponse {
  mfa_required: true
  challenge_token: string
  method: 'totp' | 'sms'
  expires_in_seconds: number
}

export type LoginResponse = AuthResponse | MFAChallengeResponse

export function isMFAChallenge(
  resp: LoginResponse,
): resp is MFAChallengeResponse {
  return (
    !!resp &&
    typeof resp === 'object' &&
    (resp as MFAChallengeResponse).mfa_required === true &&
    typeof (resp as MFAChallengeResponse).challenge_token === 'string'
  )
}
// === Phase 2 Wave 5 Agent S1 END ===

export const authApi = {
  // ØªØ³Ø¬ÙŠÙ„ Ø¯Ø®ÙˆÙ„
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>('/auth/login', credentials)
    return data
  },

  // Ù…Ø¹Ù„ÙˆÙ…Ø§Øª Ø§Ù„Ù…Ø³ØªØ®Ø¯Ù… Ø§Ù„Ø­Ø§Ù„ÙŠ
  me: async (): Promise<User> => {
    const { data } = await api.get<User>('/auth/me')
    return data
  },

  // S46: Effective permission set of the current user (powers RBAC UI)
  // Bounded on purpose -- see PERMISSIONS_FETCH_TIMEOUT_MS.
  myPermissions: async (): Promise<MyPermissionsResponse> => {
    const { data } = await api.get<MyPermissionsResponse>('/auth/me/permissions', {
      timeout: PERMISSIONS_FETCH_TIMEOUT_MS,
    })
    return data
  },

  // S49: Update the current tenant's display preferences (admin-only)
  updateTenantSettings: async (
    payload: TenantSettingsUpdate,
  ): Promise<TenantSettingsResponse> => {
    const { data } = await api.patch<TenantSettingsResponse>(
      '/auth/me/tenant-settings',
      payload,
    )
    return data
  },

  // Phase 2 Agent B1: dedicated tenant-region PATCH used by RegionSwitcher.
  updateTenantRegion: async (
    region: TenantRegion,
  ): Promise<{ region: TenantRegion }> => {
    const { data } = await api.patch<{ region: TenantRegion }>(
      '/tenants/me/region',
      { region },
    )
    return data
  },

  // ØªØºÙŠÙŠØ± ÙƒÙ„Ù…Ø© Ø§Ù„Ù…Ø±ÙˆØ±
  changePassword: async (
    payload: PasswordChangePayload
  ): Promise<PasswordChangeResponse> => {
    const { data } = await api.post<PasswordChangeResponse>(
      '/auth/change-password',
      payload
    )
    return data
  },

  // Ø·Ù„Ø¨ Ø¥Ø¹Ø§Ø¯Ø© ØªØ¹ÙŠÙŠÙ† ÙƒÙ„Ù…Ø© Ø§Ù„Ù…Ø±ÙˆØ±
  forgotPassword: async (
    payload: ForgotPasswordPayload
  ): Promise<ForgotPasswordResponse> => {
    const { data } = await api.post<ForgotPasswordResponse>(
      '/auth/forgot-password',
      payload
    )
    return data
  },

  // Ø§Ù„ØªØ­Ù‚Ù‚ Ù…Ù† ØµÙ„Ø§Ø­ÙŠØ© Ø§Ù„Ù€ token
  verifyResetToken: async (token: string): Promise<VerifyTokenResponse> => {
    const { data } = await api.get<VerifyTokenResponse>(
      '/auth/verify-reset-token',
      { params: { token } }
    )
    return data
  },

  // Ø¥Ø¹Ø§Ø¯Ø© ØªØ¹ÙŠÙŠÙ† ÙƒÙ„Ù…Ø© Ø§Ù„Ù…Ø±ÙˆØ±
  // Ù†Ø³Ø¬ÙŠÙ„ Ù…Ø³ØªØ£Ø¬Ø± Ø¬Ø¯ÙŠØ¯ Ù…Ù† Ø§Ù„Ø¨Ø¯Ø§ÙŠØ© (Public Tenant Signup)
  registerTenant: async (
    payload: TenantRegistrationPayload
  ): Promise<TenantRegistrationResponse> => {
    const { data } = await api.post<TenantRegistrationResponse>(
      '/auth/register-tenant',
      payload
    )
    return data
  },

  resetPassword: async (
    payload: ResetPasswordPayload
  ): Promise<ResetPasswordResponse> => {
    const { data } = await api.post<ResetPasswordResponse>(
      '/auth/reset-password',
      payload
    )
    return data
  },

  // Module 2.1 Phase 2 P2 — confirm an email-verification token
  verifyEmail: async (
    payload: VerifyEmailPayload
  ): Promise<VerifyEmailResponse> => {
    const { data } = await api.post<VerifyEmailResponse>(
      '/auth/verify-email',
      payload
    )
    return data
  },

  // Module 2.1 Phase 2 P2 — request a fresh verification email
  resendVerification: async (): Promise<ResendVerificationResponse> => {
    const { data } = await api.post<ResendVerificationResponse>(
      '/auth/resend-verification'
    )
    return data
  },

  // --- Part 5: Refresh Token Endpoints ----------------------------------

  // NOTE (PROD-SESSION-HARD-NAV-01 / F1.2): there is deliberately NO
  // `refresh` wrapper here any more. It was the auth store's private route to
  // POST /auth/refresh, and its `_skipAuthRefresh: true` existed only to keep
  // it out of the interceptor's single-flight queue -- which is what let two
  // rotations of the same single-use refresh token run concurrently on a hard
  // load. Rotation now has exactly ONE entry point,
  // `__authInternals.refreshAccessToken()` in lib/api.ts. Do not reintroduce a
  // second one here. (`RefreshTokenRequest` / `RefreshTokenResponse` in
  // @/types still describe the wire shape, which lib/api.ts implements.)

  // Revoke current session (single refresh token).
  logout: async (payload: LogoutRequest = {}): Promise<LogoutResponse> => {
    const { data } = await api.post<LogoutResponse>(
      '/auth/logout',
      payload,
      { _skipAuthRefresh: true } as any,
    )
    return data
  },

  // Revoke ALL active sessions for the current user.
  logoutAll: async (): Promise<LogoutResponse> => {
    const { data } = await api.post<LogoutResponse>(
      '/auth/logout-all',
      {},
      { _skipAuthRefresh: true } as any,
    )
    return data
  },
}

/**
 * Type guard: is this error an HTTP 423 "account locked" response?
 *
 * Lockout responses carry { response: { status: 423, data: { detail: {
 * locked_until, retry_after_seconds, tier, ... } } } }. Returns true only
 * for that 423 shape; false for any other status (401, etc.) and for
 * non-error inputs (null, undefined, strings, plain objects).
 */
export function isAccountLockedError(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false
  const response = (err as { response?: unknown }).response
  if (response == null || typeof response !== 'object') return false
  return (response as { status?: unknown }).status === 423
}